import axios from 'axios';
import config from './../configs';

import { getAccessToken, getRefreshToken, setRefreshedTokens, softLogout } from '../services/authService';

const instance = axios.create({
    baseURL: config[config.env].httpURL,
    responseType: 'json',
    timeout: 15000
});

// Public auth routes can return 401 for reasons that have nothing to do with whether the
// current browser session is still valid (a stale/reused verify-email or reset-password link,
// wrong login credentials, or refresh-tokens itself failing). Only a 401 from an AUTHENTICATED
// endpoint should trigger the refresh-or-logout handling below — otherwise, e.g., a logged-in
// user opening an old verification link from their inbox gets force-logged-out of an otherwise
// perfectly valid session.
const PUBLIC_AUTH_PATHS = [
    '/auth/login', '/auth/register', '/auth/forgot-password',
    '/auth/reset-password', '/auth/verify-email', '/auth/refresh-tokens'
];
const isPublicAuthRequest = (url) => PUBLIC_AUTH_PATHS.some((p) => (url || '').includes(p));

// De-dupes concurrent refresh attempts: if several requests 401 around the same moment, they
// all await this one call instead of each rotating (and invalidating each other's) the
// single-use refresh token.
let refreshPromise = null;
const refreshAccessToken = () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const refreshToken = getRefreshToken();
            if (!refreshToken) throw new Error('No refresh token available');
            const resp = await axios.create({ baseURL: config[config.env].httpURL, timeout: 15000 })
                .post('/auth/refresh-tokens', { refreshToken });
            setRefreshedTokens(resp.data);
            return resp.data;
        })().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
};

const fetchApi = async (options) => {
    const token = await getAccessToken();
    // Set Authorization on this request only — writing to instance.defaults is a shared
    // mutation, so during a logout/login transition concurrent requests could send
    // the wrong user's token (or an empty one).
    const requestOptions = {
        ...options,
        headers: {
            ...(options && options.headers),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    };

    const onSuccess = function(response) {
        return response.data;
    };

    const onError = async function(error) {
        if (error.message === 'Network Error') {
            return Promise.reject({
                error: error.response || error.message,
                // message (not just msg) — most callers read err.message; only a few
                // defensively check err.message || err.msg, so this synthesized error was
                // showing up as a generic fallback (or nothing) instead of this text.
                message: 'Couldn\'t connect server!',
                msg: 'Couldn\'t connect server!',
                // 0, not 404 — this never reached the server at all, so it must stay
                // distinguishable from a genuine backend 404 (a real "not found"). Code below
                // that treats 404 as authoritative (e.g. unpinning a sidebar project because it
                // "doesn't exist") would otherwise also fire on a plain network blip.
                code: 0
            });
        }

        const is401 = error.response?.status === 401 || error.response?.data?.code === 401;
        const publicRequest = isPublicAuthRequest(requestOptions.url);

        if (is401 && !publicRequest) {
            // Read the retry flag off the FAILED request's own config (error.config), not the
            // outer closure's original requestOptions — that object is never mutated, so
            // checking it here would always read "not yet retried" even for the retried
            // request's own failure, looping refresh+retry forever if the retry also 401s.
            const alreadyRetried = !!(error.config && error.config._retriedAfterRefresh);
            // Not already retried once → the access token probably just expired (they're
            // short-lived by design): try a silent refresh and replay the original request
            // before giving up on the session.
            if (!alreadyRetried) {
                try {
                    const refreshed = await refreshAccessToken();
                    const retryOptions = {
                        ...requestOptions,
                        _retriedAfterRefresh: true,
                        headers: { ...requestOptions.headers, Authorization: `Bearer ${refreshed.access.token}` },
                    };
                    // refreshAccessToken() rotates the refresh token server-side (old one deleted,
                    // new one issued) as a side effect of the 401 we're recovering from. A request
                    // whose own body embeds the pre-rotation refresh token (logout, which revokes
                    // it server-side) would otherwise replay that now-deleted value and silently
                    // no-op — update it to the freshly-rotated token so the retry targets the token
                    // that's actually still live.
                    if (requestOptions.data && requestOptions.data.refreshToken && refreshed.refresh?.token) {
                        retryOptions.data = { ...requestOptions.data, refreshToken: refreshed.refresh.token };
                    }
                    return instance(retryOptions).then(onSuccess).catch(onError);
                } catch (refreshErr) {
                    softLogout();
                    return Promise.reject(error.response?.data || { code: 401 });
                }
            }
            // Already retried with a freshly-refreshed token and still 401 — the session is
            // genuinely over.
            softLogout();
            return Promise.reject(error.response?.data || { code: 401 });
        }

        if (error.response?.data) {
            return Promise.reject(error.response.data);
        }

        return Promise.reject({
            error: error.response || error.message,
            message: 'Something went wrong!',
            msg: 'Something went wrong!',
            code: 0 // no response received (timeout, CORS, etc.) — see comment above
        });
    };

    return instance(requestOptions)
        .then(onSuccess)
        .catch(onError);
};

export {
    fetchApi,
    refreshAccessToken
};
